/**
 * Browser error reporting.
 *
 * Adapted from the JLAM react-vite template (`jlam-smarthealth/sentry`,
 * templates/react-vite/sentry.ts), with three deliberate departures. All three
 * matter here and not there, because there is no health data there:
 *
 * 1. **The DSN and the environment come from `runtimeEnv`, not
 *    `import.meta.env`.** The template reads a `VITE_SENTRY_DSN` baked in at
 *    build time, which would tie one image to one environment — the exact
 *    coupling besluit 0004 removed when it moved the API and Keycloak
 *    addresses into `/config.js`.
 * 2. **The environment is a named value, never `import.meta.env.MODE`.** MODE
 *    is "production" for *any* Vite production build, so the template's
 *    `environment === 'production'` test is true on staging too, and staging
 *    events would arrive labelled production.
 * 3. **URLs are redacted before they leave.** See `redactUrl`, and the two
 *    hooks below. This is the half the template does not have and the half
 *    that carries the real exposure: an unredacted URL on this platform can be
 *    an unauthenticated capability for somebody's questionnaire answers.
 *
 * The `ignoreErrors` and `denyUrls` lists are the template's and are kept as
 * they are: they were measured on 2026-09-09 against a real noise wave on
 * jlam-staging, and the host-specific `Failed to fetch` entries are narrow on
 * purpose — a bare "Failed to fetch" would also hide a genuine outage of our
 * own API.
 *
 * No `replayIntegration`, unlike the template. A session replay of a health
 * platform reconstructs a member's visit — every click, route and field order
 * — and masked text does not change what that is.
 */

import * as Sentry from '@sentry/react';

import { runtimeEnv } from '../config/runtimeEnv';
import { redactText, redactUrl } from './redactUrl';

/**
 * Whether init actually started the SDK; exported for the tests.
 *
 * No `environment !== 'development'` clause, deliberately. The template has one
 * and it is redundant here — the DSN's absence is already the off switch, and a
 * development build only has a DSN because somebody set VITE_SENTRY_DSN on
 * purpose to verify this path. Excluding 'development' would make the escape
 * hatch runtimeEnv.ts documents impossible to use.
 */
export const isEnabled = (): boolean =>
	Boolean(runtimeEnv.sentryDsn) && Boolean(runtimeEnv.environment);

export function initSentry(): void {
	// A DSN with no environment is a misconfiguration, not an absence, and it
	// is the one this whole design exists to prevent: @sentry/core defaults a
	// missing `environment` to the literal string "production", so a staging
	// deployment that set the DSN and forgot the name would file every event
	// among real members' — silently, and looking entirely healthy.
	//
	// Api/telemetry.py raises here. This does not, and the asymmetry is the
	// point rather than an oversight: a backend process that refuses to start
	// fails a deploy loudly while the previous pod keeps serving, but a throw
	// on this path unmounts the React root and hands the member the blank
	// screen that AppErrorBoundary was just written to abolish. Reporting off
	// and a loud console line is the outcome that protects both properties.
	if (runtimeEnv.sentryDsn && !runtimeEnv.environment) {
		console.error(
			'sentryDsn is set in /config.js but environment is not, so error ' +
				'reporting is OFF. Name the environment ("staging", "production"); ' +
				'it is never derived. See ' +
				'docs/decisions/0024-sentry-environment-is-named-not-derived.md.',
		);
		return;
	}
	if (!isEnabled()) return;

	Sentry.init({
		dsn: runtimeEnv.sentryDsn,
		environment: runtimeEnv.environment,
		// Named, because besluit 0025 makes integrations an allowlist and the
		// backend already is one (`auto_enabling_integrations=False`). Without
		// this the SDK installs eleven defaults, and the two that carry URLs —
		// httpContext and breadcrumbs — are the ones the hooks below exist to
		// clean. Relying on the hooks alone is a denylist: a future default that
		// attaches an address under some other key would travel untouched.
		//
		// Omitted deliberately: httpContext (sets request.url to the full href;
		// the hook below redacts what it sets, and not installing it is one less
		// thing to remember), cultureContext (locale and timezone — fingerprinting
		// for no diagnostic gain here), browserSession (session tracking we do not
		// read). `defaultIntegrations: false` is what stops the rest arriving.
		defaultIntegrations: false,
		integrations: [
			// Load-bearing: this is what implements `ignoreErrors` and `denyUrls`
			// below. Drop it and both lists silently stop filtering.
			Sentry.eventFiltersIntegration(),
			Sentry.functionToStringIntegration(),
			// window.onerror and unhandledrejection — without this an uncaught
			// error outside React's tree is never reported at all.
			Sentry.globalHandlersIntegration(),
			// Wrapped callbacks (timers, event listeners) keep their stack.
			Sentry.browserApiErrorsIntegration(),
			// `cause` chains, so a wrapped error still names what it wrapped.
			Sentry.linkedErrorsIntegration(),
			Sentry.dedupeIntegration(),
			// console is off at the source rather than dropped in the hook: its
			// arguments are whatever any component ever logged. The rest are kept
			// and redacted, so an issue still says which call failed.
			Sentry.breadcrumbsIntegration({ console: false }),
		],
		// The commit the bundle was built from, baked in by Interface/Dockerfile.
		release: import.meta.env.VITE_APP_VERSION,
		// Errors only. Tracing is a second channel with its own exposure and is
		// decided separately; see Api/telemetry.py's docstring for the argument.
		tracesSampleRate: 0,
		sendDefaultPii: false,
		ignoreErrors: [
			/^ResizeObserver loop/,
			'Can\'t find variable: __firefox__',
			'__firefox__',
			'top.GLOBALS',
			/Failed to fetch \((www\.)?googletagmanager\.com\)/,
			/Failed to fetch \(.*\.google-analytics\.com\)/,
		],
		denyUrls: [
			/^chrome-extension:\/\//,
			/^moz-extension:\/\//,
			/^safari-(web-)?extension:\/\//,
			/googletagmanager\.com/,
			/google-analytics\.com/,
		],
		beforeBreadcrumb: (breadcrumb) => {
			// Already off at the integration above; kept because a breadcrumb can
			// also be added by hand, and this is the cheaper of the two places to
			// be wrong in.
			if (breadcrumb.category === 'console') return null;
			const data = breadcrumb.data;
			if (data) {
				// xhr and fetch carry `url`; history carries `from` and `to`.
				for (const key of ['url', 'from', 'to'] as const) {
					if (key in data) data[key] = redactUrl(data[key]) ?? '';
				}
			}
			return breadcrumb;
		},
		beforeSend: (event) => {
			// httpContextIntegration is not installed, so nothing should be
			// setting request.url at all. This stays as the second lock: it is
			// the field that would carry a questionnaire capability token, and an
			// SDK upgrade that starts populating it again must not be the thing
			// that discovers this hook was removed.
			if (event.request) {
				if (event.request.url) {
					event.request.url = redactUrl(event.request.url);
				}
				// Referer is the previous URL, unredacted, by the same route.
				if (event.request.headers) delete event.request.headers.Referer;
			}

			// The exception is the largest section of a browser event and the one
			// the request and breadcrumb hooks above do not reach at all. An
			// Error's message becomes `value`, and a frame's filename is a URL by
			// construction — so `throw new Error(\`GET ${url} failed\`)`, which is
			// an ordinary idiom nobody would think twice about, would put a
			// capability token in Sentry with nothing in its way.
			//
			// Redacted rather than dropped, unlike Api/telemetry.py, and the
			// asymmetry is deliberate: a Python message usually carries values
			// this platform interpolated, while a browser message is almost always
			// the library's own sentence ("Failed to fetch", "Cannot read
			// properties of undefined") over a minified stack that says little by
			// itself. Dropping it would leave an issue nobody can act on.
			for (const value of event.exception?.values ?? []) {
				if (value.value) value.value = redactText(value.value);
				for (const frame of value.stacktrace?.frames ?? []) {
					if (frame.filename) frame.filename = redactUrl(frame.filename);
					if (frame.abs_path) frame.abs_path = redactUrl(frame.abs_path);
				}
			}
			// The route this happened on, and a captureMessage payload.
			if (event.transaction) event.transaction = redactText(event.transaction);
			if (typeof event.message === 'string') {
				event.message = redactText(event.message);
			}

			return event;
		},
	});
	Sentry.setTag('component', 'interface');
}

/**
 * Report an error, or log it where reporting is off.
 *
 * `extra` is not indexed by Sentry, so anything an operator needs to *filter*
 * on has to be a tag instead — hence the two separate bags rather than one.
 * Tags are low-cardinality labels; `extra` is diagnostic detail.
 *
 * Neither is scrubbed here: the backend allowlist drops both sections outright
 * and there is no equivalent in the browser. Pass identifiers and states to
 * either one, never member values.
 */
export function captureException(
	error: Error,
	options?: {
		extra?: Record<string, unknown>;
		tags?: Record<string, string>;
	},
): void {
	if (isEnabled()) {
		Sentry.captureException(error, {
			extra: options?.extra,
			tags: options?.tags,
		});
	} else {
		console.error('Error (Sentry disabled):', error, options);
	}
}
